import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";

import { BookingWizardDataAtom } from "@/features/BookingWizard/atoms/stores";
import { BookingWizard } from "@/features/BookingWizard/types";
import { Crementor } from "@whoosh/design-system/src/components/controls/Crementor";
import { Avatar } from "@whoosh/design-system/src/components/display";
import { Text, HStack } from "@whoosh/design-system/src/components/primitive";

import { useFlags } from "../../../../../../utils/launchDarkly";

type Props = {
  onAddUnknownGuest: () => void;
  onRemoveUnknownGuest: () => void;
  capacityOnBooking: number;
  rounds?: BookingWizard.BaseRound[];
};

export const UnknownGuestCounter = ({
  onAddUnknownGuest,
  onRemoveUnknownGuest,
  capacityOnBooking,
  rounds: activeRounds,
}: Props) => {
  const { membersCanAddUnknownGuests, memberNonPlayingHostToggle } = useFlags();
  const bookingWizardData = useAtomValue(BookingWizardDataAtom);

  const maxPlayers = capacityOnBooking ?? 4;

  const rounds = activeRounds ?? bookingWizardData?.rounds ?? [];

  const totalUnknown = rounds.filter(
    (round) => round.player.clientType === "unknownGuest"
  ).length;

  const unknownGuestMaximum =
    maxPlayers -
    (rounds.filter((round) => round.player.clientType !== "unknownGuest")
      ?.length ?? 0) +
    (memberNonPlayingHostToggle ? 1 : 0);

  const onIncrement = () => {
    if (onAddUnknownGuest && totalUnknown < unknownGuestMaximum) {
      onAddUnknownGuest();
    }
  };

  const onDecrement = () => {
    if (onRemoveUnknownGuest && totalUnknown > 0) {
      onRemoveUnknownGuest();
    }
  };
  return membersCanAddUnknownGuests ? (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      px={6}
      py={2}
      my={2}
      opacity={totalUnknown >= unknownGuestMaximum ? 0.5 : 1}
    >
      <HStack alignItems="center" gap={4}>
        <Avatar type="guest" size="sm" useIcon />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainBaseMedium"
        >{t`Unknown Guest`}</Text>
      </HStack>
      <Crementor
        min={0}
        max={unknownGuestMaximum}
        value={totalUnknown}
        onIncrement={onIncrement}
        onDecrement={onDecrement}
      />
    </HStack>
  ) : null;
};
